import React from 'react';
import clsx from 'clsx';
import { AImage, AImageAspectRatio, AImageMediaQuery, generateImageRenditions } from '../../atoms/AImage/index';
import { AScrimGradient } from '../../atoms/AScrimGradient/index';
import { AScrimGradientDirection } from '../../atoms/AScrimGradient/AScrimGradient.types';
import { MBrandElement } from '../../molecules/MBrandElement/index';
import { MTextBlockSimple } from '../../molecules/MTextBlockSimple/index';
import { MButton } from '../../molecules/MButton/index';
import { MPhotoCaption } from '../../molecules/MPhotoCaption/index';
import type { OHeroImageProps } from './OHeroImage.types';
import { OHeroImageContentAlignment, OHeroImageImageScale } from './OHeroImage.types';
import * as Styled from './OHeroImage.styles';
import { AHeaderSize, AHeaderTag } from '../../atoms/AHeader';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OHeroImage';
const TEST_ID = 'mi-web-ui-kit-OHeroImage';

export const OHeroImage: React.FC<OHeroImageProps> = ({
  contentAlignment = OHeroImageContentAlignment.Left,
  imageScale = OHeroImageImageScale.Large,
  photoCaption = false,
  secondaryButton = false,
  logo = false,
  aImage,
  aScrimGradient,
  mBrandElement,
  mTextBlockSimple,
  mButton,
  mButton2,
  mPhotoCaption,
  className,
  ...rest
}) => {
  const renditions = aImage?.image?.assetPath
    ? generateImageRenditions(
        [
          { media: AImageMediaQuery.MIN_WIDTH_XL, aspectRatio: AImageAspectRatio.WIDE_HOR, imgWidthPx: 1200 },
          { media: AImageMediaQuery.MIN_WIDTH_LG, aspectRatio: AImageAspectRatio.WIDE_HOR, imgWidthPx: 992 },
          { media: AImageMediaQuery.MIN_WIDTH_MD, aspectRatio: AImageAspectRatio.CLASSIC_HOR, imgWidthPx: 768 },
          { media: AImageMediaQuery.MIN_WIDTH_XS, aspectRatio: AImageAspectRatio.CLASSIC_HOR, imgWidthPx: 576 },
        ],
        aImage.image.assetPath
      )
    : undefined;

  const { aBody = {}, aEyebrow = {}, aHeader = {} } = mTextBlockSimple ?? {};

  const modifiedMTextBlockSimple = {
    bodyText: aBody.bodyText ?? '',
    eyebrowText: aEyebrow.eyebrowText ?? '',
    headerText: aHeader.text ?? '',
    headerSize: aHeader.size as AHeaderSize,
    headerTag: aHeader.headerTag as AHeaderTag,
  };

  const scrimDirection = aScrimGradient?.direction
    ? (aScrimGradient.direction as AScrimGradientDirection)
    : AScrimGradientDirection.BottomToTop;

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx(
        className,
        `content-alignment-${contentAlignment.toLowerCase()}`,
        `image-scale-${imageScale.toLowerCase()}`
      )}
      $contentAlignment={contentAlignment}
      $imageScale={imageScale}
      {...rest}
    >
      <Styled.ImageContainer $imageScale={imageScale}>
        {aImage && <AImage image={aImage.image} renditions={renditions} loading="eager" imgClass="hero-image" />}
        {aScrimGradient && <AScrimGradient direction={scrimDirection} />}
      </Styled.ImageContainer>

      <Styled.ContentContainer $contentAlignment={contentAlignment} className="hero-content">
        {logo && mBrandElement && (
          <Styled.BrandElementWrapper>
            <MBrandElement {...mBrandElement} />
          </Styled.BrandElementWrapper>
        )}

        {mTextBlockSimple && (
          <Styled.TextBlockWrapper>
            <MTextBlockSimple {...modifiedMTextBlockSimple} />
          </Styled.TextBlockWrapper>
        )}

        {mButton && (
          <Styled.ButtonGroup $hasSecondary={secondaryButton}>
            <MButton {...mButton} />
            {secondaryButton && mButton2 && <MButton {...mButton2} />}
          </Styled.ButtonGroup>
        )}

        {photoCaption && mPhotoCaption && (
          <Styled.PhotoCaptionWrapper>
            <MPhotoCaption {...mPhotoCaption} />
          </Styled.PhotoCaptionWrapper>
        )}
      </Styled.ContentContainer>
    </Styled.Wrapper>
  );
};

export { OHeroImageContentAlignment, OHeroImageImageScale };
